import type { AxiosRequestConfig } from 'axios';
import { authInstance } from '@/shared/api/api-instances/auth/api-instance';
import type { Page } from '@/shared/api/pagination';
import type {
  OIDCProvider,
  OIDCProviderListItem,
  OIDCProviderCreate,
  OIDCProviderUpdate,
} from '@/entities/user/user';
import type { OIDCTestResult } from '@/entities/user/user';

export class OIDCApi {
  static getProviders(options?: AxiosRequestConfig) {
    return authInstance.get<Page<OIDCProviderListItem>>(
      '/idp/settings',
      options,
    );
  }

  static getProviderNames(options?: AxiosRequestConfig) {
    return authInstance.get<{ name: string; display_name: string }[]>(
      '/idp/names',
      options,
    );
  }

  static getActiveProviderNames(options?: AxiosRequestConfig) {
    return authInstance.get<{ name: string; display_name: string }[]>(
      '/idp/names/active',
      options,
    );
  }

  static getProvider(name: string, options?: AxiosRequestConfig) {
    return authInstance.get<OIDCProvider>(`/idp/${name}`, options);
  }

  static createProvider(
    payload: OIDCProviderCreate,
    options?: AxiosRequestConfig,
  ) {
    return authInstance.post<OIDCProvider>('/idp', payload, options);
  }

  static updateProviderFull(
    name: string,
    payload: OIDCProviderCreate,
    options?: AxiosRequestConfig,
  ) {
    return authInstance.put<OIDCProvider>(`/idp/${name}`, payload, options);
  }

  static updateProvider(
    name: string,
    payload: OIDCProviderUpdate,
    options?: AxiosRequestConfig,
  ) {
    return authInstance.patch<OIDCProvider>(`/idp/${name}`, payload, options);
  }

  static deleteProvider(
    name: string,
    force: boolean = false,
    options?: AxiosRequestConfig,
  ) {
    return authInstance.delete(`/idp/${name}`, {
      ...options,
      params: { force, ...options?.params },
    });
  }

  static getTestUserInfo(key: string, options?: AxiosRequestConfig) {
    return authInstance.get<OIDCTestResult>('/oidc/user_info/test', {
      ...options,
      params: { key, ...options?.params },
    });
  }

  static async checkLoginEndpoint(
    idpName: string,
    redirectUrl: string,
    test: boolean = false,
  ): Promise<void> {
    const url = `/api/auth/v3/oidc/login/${encodeURIComponent(idpName)}?redirect_url=${encodeURIComponent(redirectUrl)}&test=${test}`;

    const response = await fetch(url, {
      method: 'GET',
      redirect: 'manual',
      signal: AbortSignal.timeout(15000),
    });

    if (response.type === 'opaqueredirect' || response.status === 0) {
      return;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        title: 'Unknown error',
        detail: '',
        type: 'Error',
      }));

      const error: any = new Error(
        errorData.title || 'OIDC login check failed',
      );
      error.response = {
        status: response.status,
        data: errorData,
      };
      throw error;
    }
  }
  static getLoginUrl(
    idpName: string,
    redirectUrl: string,
    mergeRedirectUrl?: string,
    test: boolean = false,
  ): string {
    const params = new URLSearchParams();
    params.append('redirect_url', redirectUrl);
    if (mergeRedirectUrl) {
      params.append('merge_redirect_url', mergeRedirectUrl);
    }
    params.append('test', String(test));

    return `/api/auth/v3/oidc/login/${encodeURIComponent(idpName)}?${params.toString()}`;
  }

  static getMergeLoginUrl(idpName: string, redirectUrl: string): string {
    const params = new URLSearchParams();
    params.append('redirect_url', redirectUrl);

    return `/api/auth/v3/merge/oidc/login/${encodeURIComponent(idpName)}?${params.toString()}`;
  }
}
